import { Button } from "../ui/button";
import toast from "react-hot-toast";
import { useTranslation } from "../../i18n";
import { useCampaignSql } from "../../features/super/hooks/useCampaigns";

/**
 * "Copy SQL" for the campaign settings.
 *
 * One endpoint serves both cases: the query is built from the config
 * currently in the form, including unsaved edits, so this works while creating
 * a campaign and while editing one. When editing, `campaignId` is sent too:
 * the query's frequency caps and snapshot joins are keyed by the campaign id,
 * and without it they matched nothing.
 *
 * Rendering it in the settings form rather than only on the stats panel means
 * the audience query can be sanity-checked while composing the campaign
 * instead of only after it exists.
 */
export default function CopySqlButton({ triggerType, config, campaignId }) {
  const { t } = useTranslation();
  const sqlMutation = useCampaignSql();

  const handleCopy = async () => {
    if (!triggerType) {
      toast.error(t("campaign.selectTrigger"));
      return;
    }
    try {
      const { sql } = await sqlMutation.mutateAsync({
        triggerType,
        config,
        campaignId,
      });
      await navigator.clipboard.writeText(sql);
      toast.success(t("campaign.sqlCopied"));
    } catch (err) {
      // A trigger without a generate_sql implementation answers 400 with a
      // readable reason; surface it instead of a generic failure.
      toast.error(
        typeof err === "string" && err.trim()
          ? err
          : t("campaign.sqlCopyFailed"),
      );
    }
  };

  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={handleCopy}
      disabled={sqlMutation.isPending}
    >
      {sqlMutation.isPending
        ? t("campaign.copyingSql")
        : t("campaign.copySendSql")}
    </Button>
  );
}
